import React from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {BottomCTA} from 'components/Accommodation/Offers/BottomCTA'
import {PolicySubSection} from 'components/Accommodation/Policies/types'
import {getHotelPoliciesOverlay} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {getHotelResult} from 'modules/searchApi/selectors'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Stack} from '@vio/atlas/helpers/Stack'
import {PopUp} from '@vio/atlas/PopUp'
import {TabPanel} from '@vio/atlas/src/components/designSystem/Tabs'

import {HotelContent} from './HotelContent'
import {getAvailableContentKeys, getTitle} from './utils'

const StyledTabPanel = styled(TabPanel)`
  &.--keep-mounted.--is-active {
    padding: 0;
  }
`
interface Props {
  hotelId: string | undefined
}

export const PoliciesDetailsOverlay: React.FC<Props> = ({hotelId}) => {
  const dispatch = useDispatch()
  const {formatMessage} = useIntl()
  const {isMobile} = useDeviceLayout()

  const onClose = () => dispatch(hideOverlay())

  const {data: contentHotel} = useSelector(state =>
    getHotelResult(state, hotelId)
  )

  const activeSection = useSelector(getHotelPoliciesOverlay)?.policySubSection
  const isGoodToKnow = activeSection === PolicySubSection.GoodToKnow
  if (!contentHotel) return null

  const content = (
    <div data-id="PoliciesDetailsOverlay">
      {Object.values(PolicySubSection).map(policySubSection => {
        const contentKeys = getAvailableContentKeys(
          contentHotel,
          policySubSection
        )

        if (!contentKeys.length) return null

        return (
          // Pre-render all sections so crawlers can index the content
          <StyledTabPanel
            key={policySubSection}
            value={policySubSection}
            activeTab={activeSection}
            keepMounted
          >
            <Stack space="s800">
              {contentKeys.map(key => (
                <HotelContent key={key} hotel={contentHotel} contentKey={key} />
              ))}
            </Stack>
          </StyledTabPanel>
        )
      })}
    </div>
  )

  return isMobile ? (
    <BottomSheet
      prerenderContent
      navBarProps={{
        centerContent: activeSection && formatMessage(getTitle(activeSection))
      }}
      footerContent={
        <BottomCTA
          hotelId={hotelId}
          sourceComponent="MobileHotelViewAllPoliciesStaticOfferCTA"
        />
      }
      withDragHandler={isGoodToKnow}
      snapPoints={isGoodToKnow ? {top: 'nearFull', middle: 0.7} : undefined}
      isOpen={Boolean(activeSection)}
      onClose={onClose}
      prefersReducedMotion
    >
      {content}
    </BottomSheet>
  ) : (
    <PopUp
      prerenderContent
      navBarProps={{
        centerContent: activeSection && formatMessage(getTitle(activeSection))
      }}
      isOpen={Boolean(activeSection)}
      closeAction={onClose}
      width={640}
    >
      {content}
    </PopUp>
  )
}
